import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ShieldAlert } from 'lucide-react'

import type { ProjectBlocked } from '../slices/projectDetailSlice'

/**
 * AC-7 — the blocked state. A non-member deep link receives the server's
 * 403/404 envelope and THIS card: status, code and message rendered
 * verbatim, a way back to the portfolio, and nothing else — no empty
 * workspace, no demo content, no client-side guess at why.
 */
export function ProjectBlockedState({ blocked }: { blocked: ProjectBlocked }) {
  const { t } = useTranslation('projectWorkspace')
  return (
    <div
      role="alert"
      data-testid="project-blocked"
      className="mx-auto max-w-xl rounded-lg border border-border bg-card px-6 py-8 text-center"
    >
      <ShieldAlert className="mx-auto h-6 w-6 text-muted-foreground" aria-hidden />
      <h1 className="mt-3 font-serif text-xl font-semibold text-foreground">
        {t('blocked.title')}
      </h1>
      <p className="mt-1.5 text-sm text-muted-foreground">{t('blocked.body')}</p>
      <dl className="mt-4 space-y-1 text-sm">
        <div className="flex items-center justify-center gap-2">
          <dt className="text-muted-foreground">{t('blocked.status')}</dt>
          <dd className="v-figure font-semibold tabular-nums text-foreground">{blocked.status}</dd>
        </div>
        {blocked.code && (
          <div className="flex items-center justify-center gap-2">
            <dt className="text-muted-foreground">{t('blocked.code')}</dt>
            <dd className="font-mono text-xs text-foreground">{blocked.code}</dd>
          </div>
        )}
        {blocked.message !== blocked.code && (
          <div>
            <dd className="mt-1 text-sm text-ink-soft">{blocked.message}</dd>
          </div>
        )}
      </dl>
      <Link
        to="/projects"
        className="mt-5 inline-flex items-center rounded-md border border-input bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
      >
        {t('blocked.back')}
      </Link>
    </div>
  )
}
